import { INVITE_MAX_MESSAGE_LENGTH, INVITE_MESSAGE_LENGTH_PREFIX_BYTES } from '../../constants/message';

/**
 * Serializes an invitation payload containing a Kyber public key and an optional introductory message.
 *
 * The format is:
 * - 1 byte: version (currently 1)
 * - 1 byte: message length prefix (uint8)
 * - N bytes: kyberPublicKey (raw bytes)
 * - M bytes: message (UTF-8 encoded, max length defined in INVITE_MAX_MESSAGE_LENGTH)
 *
 * @param kyberPublicKey - The sender's Kyber public key (variable length, e.g., 800–1000 bytes)
 * @param message - Optional short message to introduce or contextualize the invitation
 * @returns A Uint8Array representing the serialized invite payload
 */
export function serializeHandshakeRequestPayload(
  kyberPublicKey: Uint8Array,
  message: string
): Uint8Array {
  const encoder = new TextEncoder();
  const messageBytes = encoder.encode(message);

  if (messageBytes.length > INVITE_MAX_MESSAGE_LENGTH) {
    throw new Error(
      `Message exceeds ${INVITE_MAX_MESSAGE_LENGTH} bytes when UTF-8 encoded`
    );
  }

  const buffer = new Uint8Array(
    1 + INVITE_MESSAGE_LENGTH_PREFIX_BYTES + kyberPublicKey.length + messageBytes.length
  );

  let offset = 0;

  buffer[offset++] = 1; // version
  buffer[offset++] = messageBytes.length; // 1-byte message length

  buffer.set(kyberPublicKey, offset);
  offset += kyberPublicKey.length;

  buffer.set(messageBytes, offset);

  return buffer;
}